import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { DateTime } from 'luxon';
import useForceUpdate from '../../hooks/useForceUpdate';
import FilterInner, {
    ChipItem,
    ChipItemType,
    FilterChipMenuItem,
    FilterSelectItem,
} from './FilterInner';
import DateTimeUtility from '../../DateTimeUtility';
import {
    ChangeItem,
    CustomFilterItem,
    CustomFilterItemValue,
    CustomFilterSelectedItem,
    CustomFilterType,
    PredefinedViewItem,
    PredefinedViewValue,
    SelectedItem,
    SelectedItemCustomFilter,
    SelectedItemItem,
    SelectedItemPredefinedView,
    SelectedItemType,
} from './types';

enum SelectedItemTypeSortWeight {
    predefinedView = 2,
    customFilter = 1,
}

export interface ImperativeHandle {
    replaceWithCustomFilter: (
        nameParam: string,
        customFilter: SelectedItemItem
    ) => void;
}

interface PropsBasic {
    defaultCustomFilterItems?: CustomFilterItemValue[];
    defaultPredefinedViewItems?: string[];
    name: string;
    onChange: (items: ChangeItem[]) => void;
    persistFilter?: (
        customFilter: CustomFilterItemValue[] | null,
        predefinedViewFilter: string[] | null
    ) => void;
}

interface PropsWithPredefinedViews extends PropsBasic {
    customFilterItems?: CustomFilterItem[];
    predefinedViewItems: PredefinedViewItem[];
}

interface PropsWithCustomFiltering extends PropsBasic {
    customFilterItems: CustomFilterItem[];
    predefinedViewItems?: PredefinedViewItem[];
}

type Props = PropsWithPredefinedViews | PropsWithCustomFiltering;

const Filter = React.forwardRef<ImperativeHandle, Props>((props, ref) => {
    const {
        customFilterItems,
        defaultPredefinedViewItems,
        defaultCustomFilterItems,
        name,
        onChange,
        predefinedViewItems,
        persistFilter,
    } = props;

    const dontInformAboutDefaultItems = React.useRef(false);
    const selectedItemsRef = useRef<SelectedItem[]>([]);

    const forceUpdate = useForceUpdate();

    const setSelectedItems = (items: SelectedItem[]): void => {
        selectedItemsRef.current = items;
        forceUpdate();
    };

    useEffect((): void => {
        if (defaultCustomFilterItems) {
            defaultCustomFilterItems.forEach((item) => {
                const customFilterItem = getCustomFilterByName(item.key);
                addSelectedItemCustomFilter(item.key, {
                    value: item.value,
                    type: customFilterItem.type,
                } as SelectedItemItem);
            });
        }

        if (
            predefinedViewItems === undefined ||
            defaultPredefinedViewItems === undefined
        ) {
            return;
        }
        if (
            !defaultPredefinedViewItems.every(
                (defaultPredefinedViewItem: string) =>
                    predefinedViewItems.find(
                        (predefinedViewItem: PredefinedViewItem) =>
                            predefinedViewItem.uniqueKey ===
                            defaultPredefinedViewItem
                    )
            )
        ) {
            throw Error('defaultPredefinedView not found');
        }
        addSelectedPredefinedViews(defaultPredefinedViewItems);
        dontInformAboutDefaultItems.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect((): void => {
        if (customFilterItems !== undefined) {
            const uniqueKeysAll = customFilterItems.map(
                (customFilterItem: CustomFilterItem): string =>
                    customFilterItem.uniqueKey
            );
            const reallyUniqueKeys = uniqueKeysAll.filter(
                (uniqueKey: string, index: number, array: string[]) =>
                    array.indexOf(uniqueKey) === index
            );
            if (uniqueKeysAll.length !== reallyUniqueKeys.length) {
                throw Error('"uniqueKeys" in custom filters are not unique');
            }
        }
    }, [customFilterItems]);

    useEffect((): void => {
        if (predefinedViewItems !== undefined) {
            const uniqueKeysAll = predefinedViewItems.map(
                (predefinedViewItem: PredefinedViewItem): string =>
                    predefinedViewItem.uniqueKey
            );
            const reallyUniqueKeys = uniqueKeysAll.filter(
                (uniqueKey: string, index: number, array: string[]) =>
                    array.indexOf(uniqueKey) === index
            );
            if (uniqueKeysAll.length !== reallyUniqueKeys.length) {
                throw Error('"uniqueKeys" in predefined views are not unique');
            }
        }
    }, [predefinedViewItems]);

    const getCustomFilterByName = useCallback(
        (nameParam: string): CustomFilterItem => {
            if (customFilterItems === undefined) {
                throw Error('"customFilterItems" is undefined');
            }
            const found = customFilterItems.find(
                (customFilterItem: CustomFilterItem) =>
                    customFilterItem.uniqueKey === nameParam
            );
            if (found === undefined) {
                throw Error(
                    `CustomFilter with nameParam ${nameParam} is not found.`
                );
            }
            return found;
        },
        [customFilterItems]
    );

    const getPredefinedViewByUniqueKey = useCallback(
        (uniqueKey: string): PredefinedViewItem => {
            if (predefinedViewItems === undefined) {
                throw Error('"predefinedViewItems" is undefined');
            }
            const found = predefinedViewItems.find(
                (predefinedViewItem: PredefinedViewItem) =>
                    predefinedViewItem.uniqueKey === uniqueKey
            );
            if (found === undefined) {
                throw Error(
                    `Predefined view with nameParam ${uniqueKey} is not found.`
                );
            }
            return found;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [customFilterItems]
    );

    const customFilterSelectedItems: CustomFilterSelectedItem[] = useMemo<
        CustomFilterSelectedItem[]
    >(
        (): CustomFilterSelectedItem[] =>
            selectedItemsRef.current
                .filter(
                    (
                        selectedItem: SelectedItem
                    ): selectedItem is SelectedItemCustomFilter =>
                        selectedItem.type === SelectedItemType.customFilter
                )
                .map(
                    (
                        selectedItem: SelectedItemCustomFilter
                    ): CustomFilterSelectedItem => ({
                        name: selectedItem.uniqueKey,
                        item: selectedItem.item,
                    })
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItemsRef.current]
    );

    const getChangeOutput = (): ChangeItem[] => {
        let predefinedViewValueArray: PredefinedViewValue[][] = [];
        let customFilterValueArray: PredefinedViewValue[][] = [];

        if (predefinedViewItems !== undefined) {
            const selectedPredefinedViewItems = predefinedViewItems.filter(
                (predefinedViewItem: PredefinedViewItem): boolean =>
                    selectedItemsRef.current.some(
                        (selectedItem: SelectedItem): boolean =>
                            selectedItem.type ===
                                SelectedItemType.predefinedView &&
                            selectedItem.item.value ===
                                predefinedViewItem.uniqueKey
                    )
            );
            predefinedViewValueArray = selectedPredefinedViewItems.map(
                (
                    selectedPredefinedViewItem: PredefinedViewItem
                ): PredefinedViewValue[] => selectedPredefinedViewItem.items
            );
        }
        if (customFilterItems !== undefined) {
            const customFilterSelectedItemsWithoutEmpties = selectedItemsRef.current
                .filter(
                    (
                        selectedItem: SelectedItem
                    ): selectedItem is SelectedItemCustomFilter =>
                        selectedItem.type === SelectedItemType.customFilter
                )
                .map(
                    (
                        selectedItem: SelectedItemCustomFilter
                    ): CustomFilterSelectedItem => ({
                        name: selectedItem.uniqueKey,
                        item: selectedItem.item,
                    })
                )
                .filter(
                    (
                        customFilterSelectedItem: CustomFilterSelectedItem
                    ): boolean =>
                        customFilterSelectedItem.item !== undefined &&
                        customFilterSelectedItem.item.value !== undefined
                );
            customFilterValueArray = customFilterSelectedItemsWithoutEmpties.map(
                (
                    customFilterSelectedItemsWithoutEmpty: CustomFilterSelectedItem
                ): PredefinedViewValue[] => {
                    const customFilter: CustomFilterItem = getCustomFilterByName(
                        customFilterSelectedItemsWithoutEmpty.name
                    );
                    if (
                        customFilter.type === CustomFilterType.enum &&
                        customFilterSelectedItemsWithoutEmpty.item.type ===
                            CustomFilterType.enum
                    ) {
                        const {
                            value,
                        } = customFilterSelectedItemsWithoutEmpty.item;
                        if (value === undefined) {
                            // undefined is not possible, see filter above
                            throw Error('Unexpected exception');
                        }
                        return [
                            {
                                key: customFilter.valueName,
                                value,
                            },
                        ];
                    }
                    if (
                        customFilter.type === CustomFilterType.dateTimeRange &&
                        customFilterSelectedItemsWithoutEmpty.item.type ===
                            CustomFilterType.dateTimeRange
                    ) {
                        const {
                            value,
                        } = customFilterSelectedItemsWithoutEmpty.item;
                        if (value === undefined) {
                            // undefined is not possible, see filter above
                            throw Error('Unexpected exception');
                        }
                        return [
                            {
                                key: customFilter.valueName,
                                value,
                            },
                        ];
                    }
                    if (
                        customFilter.type === CustomFilterType.string &&
                        customFilterSelectedItemsWithoutEmpty.item.type ===
                            CustomFilterType.string
                    ) {
                        const {
                            value,
                        } = customFilterSelectedItemsWithoutEmpty.item;
                        if (value === undefined) {
                            // undefined is not possible, see filter above
                            throw Error('Unexpected exception');
                        }
                        return [
                            {
                                key: customFilter.valueName,
                                value,
                            },
                        ];
                    }
                    if (
                        customFilter.type === CustomFilterType.number &&
                        customFilterSelectedItemsWithoutEmpty.item.type ===
                            CustomFilterType.number
                    ) {
                        const {
                            value,
                        } = customFilterSelectedItemsWithoutEmpty.item;
                        if (value === undefined) {
                            // undefined is not possible, see filter above
                            throw Error('Unexpected exception');
                        }
                        return [
                            {
                                key: customFilter.valueName,
                                value,
                            },
                        ];
                    }
                    if (
                        customFilter.type === CustomFilterType.boolean &&
                        customFilterSelectedItemsWithoutEmpty.item.type ===
                            CustomFilterType.boolean
                    ) {
                        const {
                            value,
                        } = customFilterSelectedItemsWithoutEmpty.item;
                        if (value === undefined) {
                            // undefined is not possible, see filter above
                            throw Error('Unexpected exception');
                        }
                        return [
                            {
                                key: customFilter.valueName,
                                value,
                            },
                        ];
                    }
                    throw Error('Unexpected exception');
                }
            );
        }

        let outputArray: ChangeItem[] = [];
        let flatCustomFilterValueArray: ChangeItem[] = customFilterValueArray.flat();

        //  predefined values are more important then custom - so add them all
        outputArray = outputArray.concat(...predefinedViewValueArray);
        //  go through those predefined selected filters and search for dateTimeRange
        outputArray = outputArray.map((valueArray: PredefinedViewValue) => {
            const customFound = flatCustomFilterValueArray.find(
                (customValueArray: PredefinedViewValue) =>
                    customValueArray.key === valueArray.key
            );
            if (customFound !== undefined) {
                flatCustomFilterValueArray = flatCustomFilterValueArray.filter(
                    (customValueArray: PredefinedViewValue) =>
                        customValueArray.key !== valueArray.key
                );
                const customFilter: CustomFilterItem = getCustomFilterByName(
                    valueArray.key
                );
                if (customFilter.type === CustomFilterType.dateTimeRange) {
                    return checkAndMergePredefinedWithCustomDateTimeFilter(
                        valueArray,
                        customFound
                    );
                }
            }
            return valueArray;
        });
        outputArray = outputArray.concat(...flatCustomFilterValueArray);

        return outputArray;
    };

    const checkAndMergePredefinedWithCustomDateTimeFilter = (
        predefinedView: PredefinedViewValue,
        customView: PredefinedViewValue
    ): PredefinedViewValue => {
        const predefinedValue =
            predefinedView.value !== null
                ? predefinedView.value.toString()
                : null;
        const customValue =
            customView.value !== null ? customView.value.toString() : null;

        const customFrom = DateTimeUtility.parseRangeDateTimeFrom(customValue);
        const customTo = DateTimeUtility.parseRangeDateTimeTo(customValue);
        const predefinedFrom = DateTimeUtility.parseRangeDateTimeFrom(
            predefinedValue
        );
        const predefinedTo = DateTimeUtility.parseRangeDateTimeTo(
            predefinedValue
        );

        let finalFrom: DateTime | null;
        let finalTo: DateTime | null;

        if (customFrom === null && predefinedFrom === null) {
            finalFrom = null;
        } else if (customFrom === null) {
            finalFrom = predefinedFrom;
        } else if (predefinedFrom === null) {
            finalFrom = customFrom;
        } else if (customFrom <= predefinedFrom) {
            finalFrom = predefinedFrom;
        } else {
            finalFrom = customFrom;
        }

        if (customTo === null && predefinedTo === null) {
            finalTo = null;
        } else if (customTo === null) {
            finalTo = predefinedTo;
        } else if (predefinedTo === null) {
            finalTo = customTo;
        } else if (customTo >= predefinedTo) {
            finalTo = predefinedTo;
        } else {
            finalTo = customTo;
        }

        const finalValue = DateTimeUtility.generateDateTimeRangeString(
            finalFrom,
            finalTo
        );

        if (finalValue === null) {
            throw Error('Unexpected exception');
        }
        return {
            key: customView.key,
            value: finalValue,
        };
    };

    const getChangeOutputForRedux = (): {
        [SelectedItemType.predefinedView]: string[];
        [SelectedItemType.customFilter]: CustomFilterItemValue[];
    } => {
        const output: {
            [SelectedItemType.predefinedView]: string[];
            [SelectedItemType.customFilter]: CustomFilterItemValue[];
        } = {
            [SelectedItemType.predefinedView]: [],
            [SelectedItemType.customFilter]: [],
        };
        selectedItemsRef.current.forEach((item) => {
            if (item.type === SelectedItemType.predefinedView) {
                output[SelectedItemType.predefinedView].push(item.item.value);
            } else if (item.type === SelectedItemType.customFilter) {
                output[SelectedItemType.customFilter].push({
                    key: item.uniqueKey,
                    value: item.item.value,
                });
            }
        });
        return output;
    };

    useEffect(() => {
        if (dontInformAboutDefaultItems.current) {
            dontInformAboutDefaultItems.current = false;
        } else {
            if (persistFilter) {
                const outputForRedux = getChangeOutputForRedux();
                persistFilter(
                    outputForRedux[SelectedItemType.customFilter],
                    outputForRedux[SelectedItemType.predefinedView]
                );
            }
            onChange(getChangeOutput());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItemsRef.current]);

    const addSelectedItems = (newSelectedItems: SelectedItem[]): void => {
        setSelectedItems([...selectedItemsRef.current, ...newSelectedItems]);
    };

    const addSelectedPredefinedViews = (newPredefinedViews: string[]): void => {
        addSelectedItems(
            newPredefinedViews.map(
                (newPredefinedView: string): SelectedItem => ({
                    type: SelectedItemType.predefinedView,
                    item: {
                        value: newPredefinedView,
                    },
                })
            )
        );
    };

    const removeSelectedPredefinedView = (predefinedView: string): void => {
        setSelectedItems(
            selectedItemsRef.current.filter(
                (selectedItem: SelectedItem) =>
                    (selectedItem.type === SelectedItemType.predefinedView &&
                        selectedItem.item.value !== predefinedView) ||
                    selectedItem.type === SelectedItemType.customFilter
            )
        );
    };

    const changeSelectedItemForPredefinedView = (
        from: string,
        to: string
    ): void => {
        setSelectedItems(
            selectedItemsRef.current.map(
                (selectedItem: SelectedItem): SelectedItem => {
                    if (
                        selectedItem.type === SelectedItemType.predefinedView &&
                        selectedItem.item.value === from
                    ) {
                        return {
                            ...selectedItem,
                            item: {
                                value: to,
                            },
                        };
                    }
                    return selectedItem;
                }
            )
        );
    };

    const addSelectedItemCustomFilter = (
        nameParam: string,
        customFilter: SelectedItemItem
    ): void => {
        if (customFilter.type === CustomFilterType.boolean) {
            customFilter.value = true;
        }

        const selectedItem: SelectedItem = {
            type: SelectedItemType.customFilter,
            uniqueKey: nameParam,
            item: customFilter,
        };
        addSelectedItems([selectedItem]);
    };

    const removeSelectedItemCustomFilter = (key: string): void => {
        setSelectedItems(
            selectedItemsRef.current.filter(
                (selectedItem: SelectedItem) =>
                    (selectedItem.type === SelectedItemType.customFilter &&
                        selectedItem.uniqueKey !== key) ||
                    selectedItem.type === SelectedItemType.predefinedView
            )
        );
    };

    const changeSelectedItemForCustomFilter = (
        key: string,
        item: SelectedItemItem
    ): void => {
        setSelectedItems(
            selectedItemsRef.current.map(
                (selectedItem: SelectedItem): SelectedItem => {
                    if (
                        selectedItem.type === SelectedItemType.customFilter &&
                        selectedItem.uniqueKey === key
                    ) {
                        return {
                            ...selectedItem,
                            item,
                        };
                    }
                    return selectedItem;
                }
            )
        );
    };

    const predefinedViewSelectedItems: SelectedItem[] = useMemo<SelectedItem[]>(
        (): SelectedItem[] =>
            selectedItemsRef.current.filter(
                (
                    selectedItem: SelectedItem
                ): selectedItem is SelectedItemPredefinedView =>
                    selectedItem.type === SelectedItemType.predefinedView
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItemsRef.current]
    );

    const predefinedViews = useMemo<FilterSelectItem[] | undefined>(():
        | FilterSelectItem[]
        | undefined => {
        if (predefinedViewItems === undefined) {
            return undefined;
        }
        const predefinedGroupKeySelected = predefinedViewItems
            .filter((predefinedViewItem): boolean =>
                predefinedViewSelectedItems.some(
                    (predefinedViewSelectedItem): boolean =>
                        predefinedViewSelectedItem.item.value ===
                        predefinedViewItem.uniqueKey
                )
            )
            .map(
                (predefinedViewItem): string | number | undefined =>
                    predefinedViewItem.groupKey
            );

        return predefinedViewItems.map(
            (predefinedViewItem): FilterSelectItem => ({
                text: predefinedViewItem.name,
                disabled:
                    predefinedViewSelectedItems.some(
                        (predefinedViewSelectedItem): boolean =>
                            predefinedViewSelectedItem.item.value ===
                            predefinedViewItem.uniqueKey
                    ) ||
                    predefinedGroupKeySelected.includes(
                        predefinedViewItem.groupKey
                    ),
                onClick: (): void => {
                    addSelectedPredefinedViews([predefinedViewItem.uniqueKey]);
                },
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [predefinedViewSelectedItems, predefinedViewItems]);

    const customFiltering: FilterSelectItem[] | undefined = useMemo<
        FilterSelectItem[] | undefined
    >((): FilterSelectItem[] | undefined => {
        if (customFilterItems === undefined) {
            return undefined;
        }

        return customFilterItems.map(
            (customFilterItem: CustomFilterItem): FilterSelectItem => ({
                text: getCustomFilterByName(customFilterItem.uniqueKey).label,
                disabled: customFilterSelectedItems.some(
                    (
                        customFilterSelectedItem: CustomFilterSelectedItem
                    ): boolean =>
                        customFilterItem.uniqueKey ===
                        customFilterSelectedItem.name
                ),
                onClick: (): void => {
                    addSelectedItemCustomFilter(customFilterItem.uniqueKey, {
                        value: undefined,
                        type: customFilterItem.type,
                    });
                },
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customFilterItems, customFilterSelectedItems]);

    const chips = useMemo<ChipItem[]>(
        () =>
            selectedItemsRef.current
                .sort(
                    (a, b) =>
                        SelectedItemTypeSortWeight[a.type] -
                        SelectedItemTypeSortWeight[b.type]
                )
                .map(
                    (selectedItem): ChipItem => {
                        if (
                            selectedItem.type ===
                            SelectedItemType.predefinedView
                        ) {
                            return {
                                uniqueKey: selectedItem.item.value,
                                label: getPredefinedViewByUniqueKey(
                                    selectedItem.item.value
                                ).name,
                                items:
                                    predefinedViewItems === undefined
                                        ? []
                                        : predefinedViewItems.map(
                                              (
                                                  predefinedViewItem
                                              ): FilterChipMenuItem => ({
                                                  text: predefinedViewItem.name,
                                                  disabled: predefinedViewSelectedItems.some(
                                                      (
                                                          predefinedViewSelectedItem
                                                      ): boolean =>
                                                          predefinedViewSelectedItem
                                                              .item.value ===
                                                          predefinedViewItem.uniqueKey
                                                  ),
                                                  onClick: (): void => {
                                                      changeSelectedItemForPredefinedView(
                                                          selectedItem.item
                                                              .value,
                                                          predefinedViewItem.uniqueKey
                                                      );
                                                  },
                                              })
                                          ),
                                onRemove: (): void => {
                                    removeSelectedPredefinedView(
                                        selectedItem.item.value
                                    );
                                },
                                type: ChipItemType.predefinedView,
                            };
                        }
                        if (
                            selectedItem.type === SelectedItemType.customFilter
                        ) {
                            const customFilterItem: CustomFilterItem = getCustomFilterByName(
                                selectedItem.uniqueKey
                            );
                            if (
                                customFilterItem.type ===
                                    CustomFilterType.enum &&
                                selectedItem.item.type === CustomFilterType.enum
                            ) {
                                return {
                                    uniqueKey: selectedItem.uniqueKey,
                                    label: customFilterItem.label,
                                    emptyAllowed: customFilterItem.emptyAllowed,
                                    type: ChipItemType.enum,
                                    onRemove: (): void => {
                                        removeSelectedItemCustomFilter(
                                            customFilterItem.uniqueKey
                                        );
                                    },
                                    items: customFilterItem.items,
                                    onChange: (newValue): void => {
                                        changeSelectedItemForCustomFilter(
                                            customFilterItem.uniqueKey,
                                            {
                                                value: newValue,
                                                type: CustomFilterType.enum,
                                            }
                                        );
                                    },
                                    value: selectedItem.item.value,
                                };
                            }
                            if (
                                customFilterItem.type ===
                                    CustomFilterType.dateTimeRange &&
                                selectedItem.item.type ===
                                    CustomFilterType.dateTimeRange
                            ) {
                                return {
                                    uniqueKey: selectedItem.uniqueKey,
                                    label: customFilterItem.label,
                                    type: ChipItemType.dateTimeRange,
                                    onRemove: (): void => {
                                        removeSelectedItemCustomFilter(
                                            customFilterItem.uniqueKey
                                        );
                                    },
                                    onChange: (newValue): void => {
                                        changeSelectedItemForCustomFilter(
                                            customFilterItem.uniqueKey,
                                            {
                                                value: newValue,
                                                type:
                                                    CustomFilterType.dateTimeRange,
                                            }
                                        );
                                    },
                                    value:
                                        selectedItem.item.value === undefined
                                            ? null
                                            : selectedItem.item.value,
                                };
                            }
                            if (
                                customFilterItem.type ===
                                    CustomFilterType.string &&
                                selectedItem.item.type ===
                                    CustomFilterType.string
                            ) {
                                return {
                                    uniqueKey: selectedItem.uniqueKey,
                                    label: customFilterItem.label,
                                    type: ChipItemType.string,
                                    onRemove: (): void => {
                                        removeSelectedItemCustomFilter(
                                            customFilterItem.uniqueKey
                                        );
                                    },
                                    onChange: (newValue): void => {
                                        changeSelectedItemForCustomFilter(
                                            customFilterItem.uniqueKey,
                                            {
                                                value:
                                                    newValue === ''
                                                        ? undefined
                                                        : newValue,
                                                type: CustomFilterType.string,
                                            }
                                        );
                                    },
                                    value:
                                        selectedItem.item.value === undefined
                                            ? null
                                            : selectedItem.item.value,
                                };
                            }
                            if (
                                customFilterItem.type ===
                                    CustomFilterType.number &&
                                selectedItem.item.type ===
                                    CustomFilterType.number
                            ) {
                                return {
                                    uniqueKey: selectedItem.uniqueKey,
                                    label: customFilterItem.label,
                                    type: ChipItemType.number,
                                    onRemove: (): void => {
                                        removeSelectedItemCustomFilter(
                                            customFilterItem.uniqueKey
                                        );
                                    },
                                    onChange: (
                                        newValue: undefined | number
                                    ): void => {
                                        changeSelectedItemForCustomFilter(
                                            customFilterItem.uniqueKey,
                                            {
                                                value: !newValue
                                                    ? undefined
                                                    : newValue,
                                                type: CustomFilterType.number,
                                            }
                                        );
                                    },
                                    value:
                                        selectedItem.item.value === undefined
                                            ? null
                                            : selectedItem.item.value,
                                };
                            }
                            if (
                                customFilterItem.type ===
                                    CustomFilterType.boolean &&
                                selectedItem.item.type ===
                                    CustomFilterType.boolean
                            ) {
                                return {
                                    uniqueKey: selectedItem.uniqueKey,
                                    label: customFilterItem.label,
                                    type: ChipItemType.boolean,
                                    onRemove: (): void => {
                                        removeSelectedItemCustomFilter(
                                            customFilterItem.uniqueKey
                                        );
                                    },
                                    onChange: (newValue): void => {
                                        changeSelectedItemForCustomFilter(
                                            customFilterItem.uniqueKey,
                                            {
                                                value: '1',
                                                type: CustomFilterType.string,
                                            }
                                        );
                                    },
                                    value: '1',
                                };
                            }
                            throw Error('Unexpected error');
                        }
                        throw Error('Unexpected error');
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                    }
                ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            predefinedViewItems,
            predefinedViewSelectedItems,
            selectedItemsRef.current,
        ]
    );

    if (predefinedViews === undefined && customFiltering === undefined) {
        throw Error('Unexpected Exception');
    }

    const replaceWithCustomFilter = (
        nameParam: string,
        customFilter: SelectedItemItem
    ): void => {
        setSelectedItems([
            {
                type: SelectedItemType.customFilter,
                uniqueKey: nameParam,
                item: customFilter,
            },
        ]);
    };

    React.useImperativeHandle(ref, () => ({
        replaceWithCustomFilter,
    }));

    return (
        <div>
            {/* @ts-ignore is ok */}
            <FilterInner
                name={name}
                predefinedViews={predefinedViews}
                customFiltering={customFiltering}
                chips={chips}
            />
        </div>
    );
});

export default Filter;
